import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutInfo } from '@/mocks/siteData';

export default function About() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    phoneAlt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (contactForm.phoneAlt.trim() !== '') {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '', phoneAlt: '' });
      return;
    }

    if (contactForm.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    try {
      const formData = new FormData(e.currentTarget);
      const response = await fetch('https://readdy.ai/api/form/d9rd5nfnahb7382ovkpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        setContactForm({ name: '', email: '', message: '', phoneAlt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg.includes('spam') || serverMsg.includes('form data is spam') ? '提交失败，请稍后再试' : (serverMsg || '提交失败，请稍后再试'));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const siteUrl = 'https://jmcomic.com/about';

  return (
    <>
      <SEOHead
        title="关于我们 - 禁漫天堂在线"
        description="了解禁漫天堂在线的品牌故事、发展历程与核心优势。禁漫天堂在线成立于2018年，是国内领先的成人漫画在线阅读平台，拥有500万+注册用户和10万+漫画资源。"
        keywords="禁漫天堂在线,关于禁漫天堂,禁漫天堂品牌介绍,成人漫画平台,禁漫天堂联系方式"
        canonicalUrl={siteUrl}
      />
      <SchemaOrg type="Organization" data={{
        name: '禁漫天堂在线',
        url: 'https://jmcomic.com',
        description: aboutInfo.introduction,
        foundingDate: '2018',
        contactPoint: {
          '@type': 'ContactPoint',
          email: aboutInfo.contact.email,
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jmcomic.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: siteUrl },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]} />

            <section className="mb-12">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4">
                关于禁漫天堂在线
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {aboutInfo.introduction}
              </p>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 mb-6">
                <h2 className="text-base font-heading text-foreground-950 mb-2">品牌使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                <h2 className="text-base font-heading text-foreground-950 mb-2">团队介绍</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.team}</p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
                品牌数据
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {aboutInfo.reputation.map((item) => (
                  <div
                    key={item.label}
                    className="bg-background-100 border border-background-200/70 rounded-lg p-5 text-center"
                  >
                    <div className="text-2xl md:text-3xl font-heading text-accent-500 mb-1">
                      {item.value}
                    </div>
                    <div className="text-xs text-foreground-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                  <h3 className="text-base font-heading text-foreground-950 mb-4">联系信息</h3>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-mail-line text-lg text-primary-500"></i>
                      </div>
                      <span className="whitespace-nowrap">{aboutInfo.contact.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-wechat-line text-lg text-primary-500"></i>
                      </div>
                      <span className="whitespace-nowrap">{aboutInfo.contact.wechat}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-weibo-line text-lg text-primary-500"></i>
                      </div>
                      <span className="whitespace-nowrap">{aboutInfo.contact.weibo}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-time-line text-lg text-primary-500"></i>
                      </div>
                      <span className="whitespace-nowrap">{aboutInfo.contact.workingHours}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-map-pin-line text-lg text-primary-500"></i>
                      </div>
                      <span>{aboutInfo.contact.address}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                  <h3 className="text-base font-heading text-foreground-950 mb-4">在线留言</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="true">
                    <div className="flex flex-col gap-3">
                      <input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={handleInputChange}
                        placeholder="您的姓名"
                        required
                        className="w-full px-3 py-2 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleInputChange}
                        placeholder="您的邮箱"
                        required
                        className="w-full px-3 py-2 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      />
                      <input
                        type="text"
                        name="phoneAlt"
                        value={contactForm.phoneAlt}
                        onChange={handleInputChange}
                        placeholder="备用联系方式（选填）"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly={false}
                        className="honeypot-input"
                      />
                      <textarea
                        name="message"
                        value={contactForm.message}
                        onChange={handleInputChange}
                        placeholder="请输入您的留言（500字以内）"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{contactForm.message.length}/500</span>
                        <button
                          type="submit"
                          className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-send-plane-line"></i>
                          发送留言
                        </button>
                      </div>
                    </div>
                    {formStatus === 'success' && (
                      <p className="mt-3 text-sm text-accent-600">留言已成功提交，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="mt-3 text-sm text-accent-600">{formError || '提交失败，请稍后再试'}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}